import { initializeApp } from 'firebase/app'
import { GoogleAuthProvider, getAuth, inMemoryPersistence, setPersistence } from 'firebase/auth'

/**
 * Firebase is a credential broker here, and nothing else.
 *
 * Its only job is to produce a Google ID token that `POST /api/auth/google` will accept.
 * The session that follows is ours — our JWT in localStorage, our refresh rotation, our
 * revocation on sign-out. Firebase's own auth state is never read by this app.
 *
 * Hence `inMemoryPersistence`: without it Firebase keeps a second, longer-lived session
 * in IndexedDB that outlives ours. Two sessions with different lifetimes is how code ends
 * up trusting the wrong one — `auth.currentUser` would still be a signed-in user long
 * after our refresh token had been revoked.
 *
 * These values are not secret. Firebase web config ships inside every client bundle by
 * design; the security boundary is Firestore rules and our API, not this object. They
 * live in `.env` so deploy day is a config change rather than a code change, which is the
 * same reason `VITE_API_URL` is there.
 */
// Exported because the messaging service worker needs the same values and cannot read an
// environment variable of its own — `pushService` hands them over in the worker's URL.
export const config = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
}

const missing = Object.entries(config)
  .filter(([, value]) => !value)
  .map(([key]) => key)

if (missing.length) {
  throw new Error(
    `Firebase config is incomplete — missing ${missing.join(', ')}. ` +
      'Copy .env.example to .env and restart the dev server.',
  )
}

export const app = initializeApp(config)
export const auth = getAuth(app)

/**
 * Awaited by `signInWithGoogle` rather than left to run on its own.
 *
 * `setPersistence` is async, and fired and forgotten it leaves a window — however narrow —
 * where a sign-in starts under Firebase's default IndexedDB persistence and leaves behind
 * the long-lived second session the comment above exists to prevent. In practice the click
 * comes long after module load, so this promise is already settled; awaiting it costs
 * nothing and removes the ordering question entirely.
 */
export const persistenceReady = setPersistence(auth, inMemoryPersistence).catch(() => {
  // A browser with no usable storage at all still signs in — in-memory is the fallback
  // Firebase lands on anyway. Failing the sign-in over this would be the worse outcome.
})

export const googleProvider = new GoogleAuthProvider()

// Always ask which account. Phones and laptops get shared, and silently reusing the last
// Google session is how somebody orders food onto a stranger's account.
googleProvider.setCustomParameters({ prompt: 'select_account' })
